import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material';
import { format, getDaysInMonth } from 'date-fns';
import { UsageModel } from '@bymason/controller-api-hooks';
import { Card, LineChart, Text } from '@bymason-private/legacy-pattern-components';
import { getDataSize, parseMonthDateFromIso } from '@bymason/utils';
import animate from './animate';

interface UsageChartProps {
  reportMonth: Date | null;
  usageByDay: UsageModel[] | void;
  iccid?: string;
}

export default memo(function UsageChart({ reportMonth, usageByDay, iccid }: UsageChartProps) {
  const theme = useTheme();
  const [currentReportMonth, setCurrentReportMonth] = useState<Date | null>(null);
  const [currentSimIccid, setCurrentSimIccid] = useState<string | null>(null);

  const data = useMemo(
    () => ({
      labels: getLabels(reportMonth),
      series: [getValues(usageByDay || [], reportMonth)],
    }),
    [reportMonth, usageByDay]
  );

  const handleDraw = useCallback(
    (drawData) => {
      // only animate once data catches up to report-month
      // to mitigate double animations
      if (iccid === currentSimIccid || reportMonth === currentReportMonth) {
        animate(drawData, theme);
      }
    },
    [currentReportMonth, currentSimIccid, reportMonth, iccid, theme]
  );

  useEffect(() => {
    if (reportMonth) {
      setCurrentReportMonth(reportMonth);
    }
    if (iccid) {
      setCurrentSimIccid(iccid);
    }
  }, [reportMonth, iccid]);

  if (!reportMonth || !usageByDay) {
    return null;
  }

  return (
    <Card
      sx={(theme) => ({
        display: 'flex',
        flexGrow: 1,
        padding: theme.spacing(1, 0, 0, 0),
        width: 'fit-content',
        [theme.breakpoints.down(1140)]: {
          minWidth: 678,
          width: '100%',
        },
      })}
      variant='outlined'
    >
      {!!data.series[0].length && (
        <LineChart
          sx={{
            flexGrow: 1,
            minHeight: 272,
          }}
          data={data}
          onDraw={handleDraw}
          options={{
            axisY: {
              labelInterpolationFnc: (value: number) => {
                const [numValue, numUnit] = getDataSize(value, 1);

                return `${numValue} ${numUnit}`;
              },
              offset: 64,
            },
            axisXTitleOptions: {
              axisTitle: 'Days of the month',
              offset: {
                y: 48,
              },
            },
            axisYTitleOptions: {
              axisTitle: 'Data usage',
              offset: {
                x: 32,
                y: 0,
              },
            },
            fullWidth: true,
            chartPadding: {
              bottom: 48,
              left: 48,
              right: 32,
              top: 24,
            },
            lineSmooth: true,
            low: getLowestValue(usageByDay || []),
            onTransformTooltipText: (value) => {
              const [numValue, numUnit] = getDataSize(value, 2);

              return `${numValue} ${numUnit}`;
            },
            showArea: true,
          }}
        />
      )}
      {!data.series[0].length && (
        <Text flex={1} alignSelf='center' align='center'>
          No data
        </Text>
      )}
    </Card>
  );
});

//
//
// helpers
//
//

function getLabels(reportMonth: Date | null) {
  if (!reportMonth) {
    return [];
  }

  const daysInMonth = Array(getDaysInMonth(reportMonth))
    .fill('0')
    .map((_, idx) => `${idx + 1}`);

  return daysInMonth;
}

function getLowestValue(usageByDay: UsageModel[]) {
  if (!usageByDay.length) {
    return 0;
  }

  const lowestVal = usageByDay.reduce((lowest, day) => {
    return Math.min(lowest, day.total);
  }, Number.MAX_SAFE_INTEGER);

  return lowestVal;
}

function getValues(usageByDay: UsageModel[], reportMonth: Date | null): Chartist.IChartistSeriesData[] {
  if (reportMonth && usageByDay.length > 0) {
    const dailyData = Array(getDaysInMonth(reportMonth)).fill('undefined');

    usageByDay.forEach((data) => {
      const index = Number(format(parseMonthDateFromIso(data.day), 'd')) - 1;
      dailyData[index] = { meta: format(parseMonthDateFromIso(data.day), 'MMM do'), value: data.total };
    });

    return dailyData;
  }
  return [];
}
